import { FullViewSwitch } from '@planning/components/atoms/FullViewSwitch'
import { useVesselVisitTableActions } from '@planning/hooks/useVesselVisitTableActions'
import {
  DischargeListTableColumnsDto,
  VesselVisitDischargeListViewStore,
} from '@planning/pages/VesselVisit/Stores/VesselVisitOrderListViewStore'
import { getDischargeListColumns } from '@planning/pages/VesselVisit/tableConfigs/dischargeListColumns'
import { useTranslate } from '@tolgee/react'
import { TomTable, useTomTable } from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { TomTableToolbarAlertBanner } from 'modules/ui/src/theme/components/table/organisms/TomTableToolbarAlertBanner'
import { FC, useEffect, useMemo } from 'react'
import { VisitDashboardTableDetailPanel } from '../../atoms/VisitDashboardTableDetailPanel'
import { OrderListMenuOptions } from '../../molecules/OrderListMenuOptions'
import { ContainerYardOperationViewStore } from '../../OrderGrouping'

type Props = {
  vesselVisitDischargeListViewStore: VesselVisitDischargeListViewStore
  containerYardOperationViewStore: ContainerYardOperationViewStore
}

export const VesselVisitContainerDischargeTable: FC<Props> = observer(
  ({ vesselVisitDischargeListViewStore, containerYardOperationViewStore }) => {
    const { t } = useTranslate()

    const getTableActions = useVesselVisitTableActions<DischargeListTableColumnsDto>()

    const vesselVisitItem = vesselVisitDischargeListViewStore.vesselVisitItem

    const columns = useMemo(() => getDischargeListColumns(t), [t])

    const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
      'planning/vessel-visit-container-discharge',
    )

    const table = useTomTable<DischargeListTableColumnsDto>(
      {
        columns,
        data: vesselVisitDischargeListViewStore.ordersTableData,
        getRowId: row => row.id.toString(),

        renderToolbarAlertBannerContent: ({ table }) => {
          const selectedRows = table.getSelectedRowModel().rows
          const dynamicActions = getTableActions(selectedRows)

          if (!dynamicActions.length) {
            return null
          }

          if (vesselVisitItem.isVesselVisitBlockedForEdit) {
            dynamicActions.forEach(action => {
              action.disabled = true
            })
          }

          return <TomTableToolbarAlertBanner table={table} tableActions={dynamicActions} />
        },

        renderTopToolbarCustomActions: () => (
          <FullViewSwitch
            onChange={val => vesselVisitDischargeListViewStore.setIsFullView(table, val)}
            value={vesselVisitDischargeListViewStore.isFullView}
          />
        ),
        renderRowActions: ({ row }) => {
          const orderId = row.original.id
          const order = vesselVisitItem?.discharge?.containerOrders.find(o => o.id === orderId)
          const journey = order?.containerJourney

          return OrderListMenuOptions({ order, journey, containerYardOperationViewStore })
        },
        getRowCanExpand: () => true,
        renderDetailPanel: ({ row }) => <VisitDashboardTableDetailPanel row={row} />,
        muiTableBodyRowProps: ({ row }) => ({
          id: `discharge-list-row-${row.original.number}`,
        }),
      },
      undefined,
      undefined,
      { columns, savedPreferences, onPreferencesChange },
    )

    useEffect(() => {
      vesselVisitDischargeListViewStore.setIsFullView(table, false)
    }, [table, vesselVisitDischargeListViewStore])

    return <TomTable table={table} />
  },
)
